<template>
    <form @submit.prevent="showApprovedModal(true)">
    <div class="xl">
        <div class="flex items-center justify-between pt-2 pb-1">
            <div class="pt-2 pb-1 text-2xl font-bold">Products</div>

            <jet-button
                v-if="isAdmin && refund.status === 'new'"
                type="button"
                class="text-sm px-4 py-2"
                @click="refundEntireOrder"
            >
                Refund All
            </jet-button>
        </div>
        
        <div v-if="refund.receipt_image" class="pb-1">
            <span class="font-bold">Receipt Image</span>

            <div class="p-2 mt-3 relative" style="width: 20%">
                <a :href="'/storage/' + refund.receipt_image" target="_blank">
                    <img :src="'/storage/' + refund.receipt_image" class="img-fluid" />
                </a>
            </div>
        </div>
    </div>

    <div class="rounded-t">
        <table class="table w-full">
            <thead>
                <tr class="h-10 align-bottom bg-gray-light border">
                    <th class="text-center" style="width: 15%;">Product Image</th>
                    <th class="text-left" style="width: 10%;">Product</th>
                    <th class="text-left">Defect</th>
                    <th class="text-left">Notes</th>
                    <th class="text-center" style="width: 15%;">Refund Image</th>
                    <th class="text-right whitespace-nowrap">Request Qty</th>
                    <th class="text-right whitespace-nowrap">Refund Qty</th>
                    <th class="text-right">Price</th>
                    <th v-if="!refund.order?.tax_exempt && !refund.order?.customer?.tax_exempt" class="text-center">
                        Tax
                    </th>
                    <th class="text-right">Total</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(item) in refund_items" :key="item.id">
                    <td class="text-left">
                        <img v-if="item.product.image != null" :src="'/storage/' + item.product.image" class="mx-auto max-h-20 border border-transparent rounded-md" alt="Product Image" loading="lazy" />
                        <img v-show="item.product.image == null" src="/images/product-placeholder.png" class="mx-auto max-h-20 rounded-md" alt="No Image" loading="lazy" />
                    </td>
                    <td class="text-left">
                        {{ item.product.title }}
                        <span v-if="item.product.description"> - {{ item.product.description }}</span>
                        <span v-if="item.product.size"> - {{ item.product.size }}</span>
                    </td>
                    <td class="text-left">
                        {{ item.formatted_return_reason }}
                    </td>
                    <td class="text-justify">
                        <div v-if="item.order_item?.parent_id">
                            Part of {{ item.order_item?.parent_item?.title }}
                        </div>
                        {{ item.return_note }}
                    </td>
                    <td class="text-center">
                        <a v-if="item.item_image != null && item.item_image != ''" :href="'/storage/' + item.item_image" target="_blank">
                            <img :src="'/storage/' + item.item_image" class="mx-auto max-h-20 border border-transparent rounded-md" alt="Product Image" loading="lazy" />
                        </a>
                    </td>
                    <td class="text-right">
                        {{ item.quantity }}
                    </td>
                    <td class="text-right">
                        <jet-input-quantity
                            v-if="refund.status === 'new'"
                            type="number"
                            v-model.number="item.refunded_quantity"
                            class="w-full quantity"
                            :step="0.25"
                            :max="item.quantity"
                            :min="0"
                            required
                        />
                        <div v-else>{{ item.refunded_quantity }}</div>
                    </td>
                    <td class="text-right">
                        <div
                            :style="{
                                'padding-top': (item.price != item.order_item?.price) ? '1.25rem' : null,
                                'padding-bottom': (item.price != item.order_item?.price) ? '1.25rem' : null
                            }"
                        >
                            <jet-input
                                v-if="refund.status === 'new'"
                                type="number"
                                class="w-full price text-right"
                                v-model.number="item.price"
                                :step="0.01"
                                required
                            />
                            <div v-else>{{ toCurrency(item.price) }}</div>

                            <div
                                v-if="item.price != item.order_item?.price"
                                class="text-sm text-gray-500"
                                style="margin-bottom: -1.25rem;"
                            >
                                {{ toCurrency(item.order_item?.price) }}
                            </div>
                        </div>
                    </td>
                    <td 
                        class="text-center"
                        v-if="!refund.order?.tax_exempt && !refund.order?.customer?.tax_exempt"    
                    >
                        <img v-if="item.order_item?.tax" src="/images/icons/check.svg" width="24" height="24" class="mx-auto"/>
                        <img v-else src="/images/icons/x.svg" width="24" height="24" class="mx-auto" />
                    </td>
                    <td class="text-right">
                        {{ toCurrency(lineDiscountedAmount(item)) }}
                    </td>
                </tr>
            </tbody>
        </table>
    </div>

    <div v-if="refund.status !== 'rejected'" class="flex flex-col md:flex-row justify-between">
        <div class="flex"></div>

        <div class="mt-1 self-end">
            <table class="totals">
                <tbody>
                    <tr>
                        <td colspan="2" class="text-right font-bold">Subtotal:</td>
                        <td class="text-right whitespace-nowrap">
                            <span>{{ toCurrency(subtotal) }}</span>
                        </td>
                    </tr>
                    <tr v-if="refund.order.delivery_fee > 0 || refund.order.shipping_fee > 0">
                        <td colspan="2" class="text-right font-bold">Delivery Fee:</td>
                        <td class="px-2 py-1 text-right">
                            <div v-if="refund.status === 'new'" class="flex justify-end">
                                <div class="py-1 px-3 border border-r-0 rounded-r-none rounded-l">$</div>
                                <jet-input 
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    class="rounded-l-none w-24 text-right" 
                                    v-model.number="form.delivery_fee" 
                                    :placeholder="showDeliveryPlaceholder ? (refund.order.delivery_fee ?? refund.order.shipping_fee) : ''"
                                    @focus="showDeliveryPlaceholder = true"
                                    @blur="showDeliveryPlaceholder = false"
                                />
                            </div>
                            <div v-else>
                                <span>{{ toCurrency(refund.delivery_fee ?? 0) }}</span>
                            </div>
                        </td>
                    </tr>
                    <tr v-if="!isTaxExempt">
                        <td colspan="2" class="text-right font-bold">Tax:</td>
                        <td class="text-right whitespace-nowrap">
                            <span>{{ toCurrency(tax) }}</span>
                        </td>
                    </tr>
                    <tr v-if="hasPercentPromo && refund.order?.applied_promo_code">
                        <td colspan="2" class="text-right">
                            <div class="font-bold">Promo Code:</div>
                            <div class="text-gray-500">
                                {{ refund.order.applied_promo_code.code }}
                                <span v-if="refund.order.promo_code_percent">({{ refund.order.promo_code_percent }}%)</span>
                                <span> — included in line totals</span>
                            </div>
                        </td>
                        <td></td>
                    </tr>
                    <tr v-if="promoCodeAdjustment > 0">
                        <td colspan="2" class="text-right">
                            <div class="font-bold">Promo Code Adjustment:</div>
                            <div class="text-gray-500">
                                {{ refund.order?.applied_promo_code?.code }}
                            </div>
                        </td>
                        <td class="text-right whitespace-nowrap">
                            <span>-{{ toCurrency(promoCodeAdjustment) }}</span>
                        </td>
                    </tr>
                    <tr>
                        <td colspan="2" class="text-right font-bold">Refund Total:</td>
                        <td class="text-right whitespace-nowrap">
                            <span v-if="refund.status === 'new'">{{ toCurrency(refundTotal) }}</span>
                            <span v-else>{{ toCurrency(refund.amount) }} </span>
                        </td>
                    </tr>
                    <tr v-if="refund.status === 'new' && !isCustomer">
                        <th class="px-2 py-1 text-right text-red-600">
                            <jet-button>
                                Refund
                            </jet-button>
                        </th>
                        <td class="px-2 py-1 text-right text-red-600">
                            <jet-secondary-button type="button" @click="showCloseWithoutRefundModal(true)">
                                Close without refund
                            </jet-secondary-button>
                        </td>
                        <td class="px-2 py-1 text-right text-red-600">
                            <jet-danger-button type="button" @click="showRejectedModal(true)">
                                Reject
                            </jet-danger-button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <jet-confirmation-modal :show="showingApprovedModal" @close="resetApprovedModal(); showApprovedModal(false)">
        <template #title>
            Approve Refund
        </template>

        <template #content>
            Are you sure you would like to approve this refund?

            <div v-if="requiresRefundMethod">
                <jet-label value="Refund Method" class="mb-0" />
                <multiselect
                    v-model="refundMethod"
                    :options="refundMethodOptions"
                />
                <jet-input-error v-if="refundMethodError" :message="refundMethodError" class="mt-1" />
            </div>

        </template>

        <template #footer>
            <jet-button @click="approveRefundOrder">
                Yes
            </jet-button>

            <jet-tertiary-button class="ml-2" @click="resetApprovedModal(); showApprovedModal(false)">
                No
            </jet-tertiary-button>
        </template>
    </jet-confirmation-modal>

    <jet-confirmation-modal :show="showingRejectedModal" @close="showRejectedModal(false)">
        <template #title>
            Reject Refund
        </template>

        <template #content>
            Are you sure you would like to reject this refund?
            <textarea 
                v-model="form.reviewed_note"
                class="w-full resize-none mt-2" 
                rows="4" 
                placeholder="Rejection Notes..."
                ref="reviewed_note">
            </textarea>
        </template>

        <template #footer>
            <jet-button @click="rejectRefundOrder">
                Yes
            </jet-button>

            <jet-tertiary-button class="ml-2" @click="showRejectedModal(false)">
                No
            </jet-tertiary-button>
        </template>
    </jet-confirmation-modal>

    <jet-confirmation-modal :show="showingCloseWithoutRefundModal" @close="resetCloseWithoutRefundModal(false)">
        <template #title>
            Close without refund
        </template>

        <template #content>
            <p class="mb-2">
                Close this credit request without issuing a refund, credit order, or promo? Defect and line data will be kept for reporting.
            </p>
            <textarea
                v-model="closeWithoutRefundNote"
                class="w-full resize-none mt-2"
                rows="4"
                placeholder="Optional notes…"
            />
        </template>

        <template #footer>
            <jet-button @click="closeWithoutRefundOrder">
                Yes, close without refund
            </jet-button>

            <jet-tertiary-button class="ml-2" @click="resetCloseWithoutRefundModal(false)">
                Cancel
            </jet-tertiary-button>
        </template>
    </jet-confirmation-modal>
    </form>
</template>

<script>
import { usePage, useForm } from '@inertiajs/inertia-vue3';
import UseUserAuth from '@/Composables/UseUserAuth';
import { round2, TAX_RATE } from '@/Composables/useOrderTotals';
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputQuantity from '@/Jetstream/InputQuantity';
import JetInputError from '@/Jetstream/InputError';
import JetButton from '@/Jetstream/Button';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import Multiselect from '@vueform/multiselect';

import '@vueform/multiselect/themes/default.css';

export default {
    setup () {
        const { isAdmin, isCustomer } = UseUserAuth(usePage().props.value.user);

        return { isAdmin, isCustomer, round2, TAX_RATE }
    },

    components: {
        JetInput,
        JetInputQuantity,
        JetInputError,
        JetButton,
        JetConfirmationModal,
        JetSecondaryButton,
        JetDangerButton,
        JetDialogModal,
        JetLabel,
        JetTertiaryButton,
        Multiselect
    },

    props: [
        'form',
        'refund_items',
        'refund'
    ],

    data() {
        return {
            showingRejectedModal: false,
            showingApprovedModal: false,
            showingCloseWithoutRefundModal: false,
            closeWithoutRefundNote: '',
            maxRefundAmount: this.form.amount,
            refundMethod: null,
            refundMethodError: null,
            refundMethodOptions: [
                {
                    label: 'Credit Card',
                    value: 'credit_card'
                },
                {
                    label: 'Promo Code',
                    value: 'promo_code'
                }
            ],

            selectedRefundOrderItemId: null,
            showDeliveryPlaceholder: false,
        }
    },

    computed: {
        requiresRefundMethod () {
            return this.refund.order?.is_wholesale == 0;
        },

        isTaxExempt () {
            return !!(this.refund.order?.tax_exempt || this.refund.order?.customer?.tax_exempt);
        },

        hasPercentPromo () {
            const pct = this.refund.order?.promo_code_percent;
            return pct != null && pct !== '';
        },

        hasDollarPromo () {
            const order = this.refund.order;
            if (!order) return false;
            const hasPromo = !!(order.promo_code_id || order.applied_promo_code);
            return hasPromo && Number(order.promo_code_amount) > 0 && order.promo_code_percent == null;
        },

        delivery_fee () {
            const val = parseFloat(this.form.delivery_fee ?? this.refund.delivery_fee);
            return isNaN(val) ? 0 : val;
        },

        // Percentage promos discount delivery before tax (matches Order::getOrderTotalsStruct).
        deliveryFeeAfterDiscount () {
            if (!this.hasPercentPromo) {
                return this.round2(this.delivery_fee);
            }
            const gross = this.round2(this.delivery_fee);
            const pct = Number(this.refund.order.promo_code_percent) || 0;
            return this.round2(gross - this.round2(gross * pct / 100));
        },

        subtotal () {
            let total = 0;
            for (const item of this.refund_items) {
                total += this.lineDiscountedAmount(item);
            }
            return this.round2(total);
        },

        tax () {
            if (this.isTaxExempt) return 0;
            let tax = 0;
            for (const item of this.refund_items) {
                const lineAmount = this.lineDiscountedAmount(item);
                if (item.order_item?.tax) {
                    tax += this.round2(lineAmount * this.TAX_RATE);
                }
            }
            tax += this.round2(this.deliveryFeeAfterDiscount * this.TAX_RATE);
            return this.round2(tax);
        },

        totalBeforeAdjustment () {
            return this.round2(this.subtotal + this.tax + this.deliveryFeeAfterDiscount);
        },

        // Dollar promos only: proportional post-tax share (not applied per line).
        promoCodeAdjustment () {
            if (!this.hasDollarPromo) return 0;
            const before = this.totalBeforeAdjustment;
            if (before <= 0) return 0;

            const order = this.refund.order;
            const promoTotal = Number(order.promo_code_total ?? order.promo_code_amount) || 0;
            if (promoTotal <= 0) return 0;

            const orderTotal = Number(order.total) || 0;
            const appliedCredit = Number(order.applied_credit_amount) || 0;
            const fullBeforeDiscount = this.round2(orderTotal + appliedCredit + promoTotal);
            if (fullBeforeDiscount < 0.0001) return 0;

            const scaled = this.round2(before * (orderTotal / fullBeforeDiscount));
            return this.round2(before - scaled);
        },

        refundTotal () {
            return this.round2(this.totalBeforeAdjustment - this.promoCodeAdjustment);
        }
    },

    methods: {
        /**
         * Discounted refund line amount for one item.
         * Uses backend line_discounted_total when price is unchanged; otherwise price × qty.
         */
        lineDiscountedAmount (item) {
            const qty = Number(item.refunded_quantity) || 0;
            const price = Number(item.price) || 0;
            const orderPrice = item.order_item?.price != null ? Number(item.order_item.price) : null;
            const priceEdited = orderPrice != null && price !== orderPrice;

            if (!priceEdited && item.line_discounted_total != null) {
                const orderQty = Number(item.order_item?.quantity) || 0;
                if (orderQty > 0) {
                    return this.round2((item.line_discounted_total / orderQty) * qty);
                }
            }

            return this.round2(price * qty);
        },

        showApprovedModal(value) {
            if(!this.form.processing){
                this.showingApprovedModal = value;
            }
        },

        resetApprovedModal () {
            this.showApprovedModal(false);
            this.refundMethod = null;
            this.refundMethodError = null;
        },

        approveRefundOrder() {
            if (this.requiresRefundMethod && !this.refundMethod) 
            {
                this.refundMethodError = "A refund method is required."
                return false;
            }

            const form = useForm({
                refund_id: this.form.id,
                refunded_amount: this.refundTotal, 
                refund_method: this.refundMethod,
                refund_items: this.refund_items,
                delivery_fee: this.deliveryFeeAfterDiscount,
            })
            this.resetApprovedModal();
            form.post(route('refunds.approve'));
        },

        showRejectedModal(value) {
            this.showingRejectedModal = value;
        },

        rejectRefundOrder() {
            const form = useForm({
                refund_id: this.form.id,
                reviewed_note: this.form.reviewed_note
            })
            this.showRejectedModal(false);
            form.post(route('refunds.reject'));
        },

        showCloseWithoutRefundModal(value) {
            this.showingCloseWithoutRefundModal = value;
        },

        resetCloseWithoutRefundModal(value) {
            this.showingCloseWithoutRefundModal = value;
            if (!value) {
                this.closeWithoutRefundNote = '';
            }
        },

        closeWithoutRefundOrder() {
            const form = useForm({
                refund_id: this.form.id,
                reviewed_note: this.closeWithoutRefundNote || null,
            });
            this.resetCloseWithoutRefundModal(false);
            form.post(route('refunds.close-without-refund'));
        },

        refundEntireOrder() {
            if (!this.refund_items || !this.refund_items.length) return;

            for (const item of this.refund_items) {
                item.refunded_quantity = item.quantity;
            }
        },
    }
}
</script>

<style scoped>
.totals th, .totals td {
    padding: 0.25rem 0.5rem ;
}
</style>
